import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Typography, Stack, Box, IconButton, useTheme, Zoom, TextField, alpha
} from '@mui/material';
import { PersonAddAlt1 as PersonAddIcon, Close as CloseIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { ParceirosService, IDetalheParceiros } from '../../shared/services/api/Venda/ParceiroService';
import { PDVThemeColors } from './PDVThemes';

interface DialogNovoParceiroProps {
  open: boolean;
  onClose: () => void;
  onCreated: (idparceiro: string, nome: string) => void;
  colors: PDVThemeColors;
}

const apenasDigitos = (valor: string) => valor.replace(/\D/g, '');

const formatarCpf = (valor: string) => {
  const digitos = apenasDigitos(valor).slice(0, 11);
  return digitos
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const formatarTelefone = (valor: string) => {
  const digitos = apenasDigitos(valor).slice(0, 11);
  if (digitos.length <= 10) {
    return digitos.replace(/(\d{2})(\d{4})(\d{0,4})/, (_, a, b, c) => c ? `(${a}) ${b}-${c}` : `(${a}) ${b}`);
  }
  return digitos.replace(/(\d{2})(\d{5})(\d{0,4})/, (_, a, b, c) => c ? `(${a}) ${b}-${c}` : `(${a}) ${b}`);
};

// Dialog de cadastro rápido de cliente direto na tela de pagamento — coleta
// só o essencial para identificar o cliente na venda; os demais campos do
// cadastro completo de parceiro ficam com valores padrão (consumidor final).
export const DialogNovoParceiro: React.FC<DialogNovoParceiroProps> = ({ open, onClose, onCreated, colors }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [celular, setCelular] = useState('');
  const [email, setEmail] = useState('');
  const [erroNome, setErroNome] = useState('');
  const [salvando, setSalvando] = useState(false);

  const nomeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setNome('');
      setCpf('');
      setCelular('');
      setEmail('');
      setErroNome('');
      setSalvando(false);
      setTimeout(() => nomeRef.current?.focus(), 80);
    }
  }, [open]);

  const salvar = async () => {
    if (!nome.trim()) {
      setErroNome('Nome é obrigatório');
      nomeRef.current?.focus();
      return;
    }

    setSalvando(true);

    const dados: IDetalheParceiros = {
      nome: nome.trim(),
      cpf: apenasDigitos(cpf),
      fantasia: '',
      rg: '',
      telefone: '',
      celular: apenasDigitos(celular),
      endereco: '',
      numero: '',
      bairro: '',
      idmunicipio: '',
      cep: '',
      email: email.trim(),
      obs: '',
      cfop: 0,
      contribuinte: 9,
      presenca: 1,
      consumidorfinal: 1,
      emailnfe: email.trim(),
      enviaremail: false,
    };

    const resultado = await ParceirosService.create(dados);
    setSalvando(false);

    if (resultado instanceof Error) {
      setErroNome(resultado.message);
      return;
    }

    onCreated(resultado, dados.nome);
  };

  const aoPressionarEnterSalvar = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      salvar();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={salvando ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 220 }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `0.5px solid ${borderColor}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '8px',
              background: colors.primaryGradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PersonAddIcon sx={{ fontSize: 18, color: colors.textOnPrimary }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Adicionar cliente
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={salvando} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <TextField
            inputRef={nomeRef}
            label="Nome"
            value={nome}
            onChange={(e) => { setNome(e.target.value); if (erroNome) setErroNome(''); }}
            onKeyDown={aoPressionarEnterSalvar}
            error={!!erroNome}
            helperText={erroNome}
            size="small"
            fullWidth
            autoFocus
          />
          <TextField
            label="CPF"
            value={cpf}
            onChange={(e) => setCpf(formatarCpf(e.target.value))}
            onKeyDown={aoPressionarEnterSalvar}
            size="small"
            fullWidth
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            label="Celular"
            value={celular}
            onChange={(e) => setCelular(formatarTelefone(e.target.value))}
            onKeyDown={aoPressionarEnterSalvar}
            size="small"
            fullWidth
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            label="E-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={aoPressionarEnterSalvar}
            size="small"
            fullWidth
            type="email"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={salvar}
          disabled={salvando}
          startIcon={<CheckCircleIcon />}
          sx={{
            borderRadius: '10px',
            py: 1,
            background: colors.primaryGradient,
            color: colors.textOnPrimary,
            '&:hover': { background: colors.hoverGradient },
          }}
        >
          {salvando ? 'Salvando...' : 'Adicionar à venda'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
